// Polyfills and CSS must be imported first
import './polyfills';
import './index.css';
import '@interchain-ui/react/styles';

import { StrictMode, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ChainProvider } from '@cosmos-kit/react';
import { chains, assets } from 'chain-registry';
import { Registry } from '@cosmjs/proto-signing';
import { AminoTypes, defaultRegistryTypes, createDefaultAminoConverters } from '@cosmjs/stargate';
import * as auctionClient from '@chalabi/gravity-bridgejs/dist/codegen/auction/client';
import * as gravityClient from '@chalabi/gravity-bridgejs/dist/codegen/gravity/client';
import { MsgSendToEth, MsgRequestBatch, type MsgSendToEth as MsgSendToEthType, type MsgSendToEthAmino, type MsgRequestBatch as MsgRequestBatchType, type MsgRequestBatchAmino } from '@chalabi/gravity-bridgejs/dist/codegen/gravity/v1/msgs';
import type { SignerOptions } from '@cosmos-kit/core';
import { ErrorBoundary } from './components/ErrorBoundary';
import { getSupportedWallets } from './config/wallets';
import { getConstants, type AppConstants } from './config/constants';
import App from './App';

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60000, // 1 minute
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Inner component that uses hooks requiring QueryClient
export function AppWithChainProvider(): JSX.Element {
  const [constants, setConstants] = useState<AppConstants | null>(null);
  const [error, setError] = useState<Error | null>(null);

  // Fetch constants from backend on mount
  useEffect(() => {
    getConstants()
      .then(setConstants)
      .catch((err) => {
        console.error('Failed to load chain constants:', err);
        setError(err as Error);
      });
  }, []);

  // Show loading state while fetching constants
  if (constants === null && error === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading chain configuration...</p>
        </div>
      </div>
    );
  }

  // Show error state if constants failed to load
  if (error !== null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <svg className="h-12 w-12 text-red-600 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h2 className="text-xl font-semibold text-red-900 mb-2">Failed to Load Configuration</h2>
            <p className="text-red-700 mb-4">Unable to connect to backend server.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Filter chains to only include gravitybridge and customize its endpoints
  // IMPORTANT: We override the chain's apis property to prevent cosmos-kit from
  // falling back to chain-registry's default public RPC endpoints
  const gravityChain = chains.find((c) => c.chain_name === 'gravitybridge');
  const customizedChains = gravityChain !== undefined && constants !== null
    ? [{
        ...gravityChain,
        chain_id: constants.gravityChainId,
        // Override the apis to only use our configured endpoints
        apis: {
          rpc: [{ address: constants.gravityRpc, provider: 'Gravity Station' }],
          rest: [{ address: constants.gravityRest, provider: 'Gravity Station' }],
          grpc: [{ address: constants.gravityGrpc, provider: 'Gravity Station' }],
        },
      }]
    : chains;

  // Configure signer options with default Cosmos SDK types + auction types + gravity types
  const signerOptions: SignerOptions = {
    signingStargate: () => {
      // Merge default registry types with auction and gravity types
      const registry = new Registry([
        ...defaultRegistryTypes,
        ...auctionClient.auctionProtoRegistry,
        ...gravityClient.gravityProtoRegistry,
      ]);
      // Merge default amino converters with auction and gravity amino converters
      // Override MsgSendToEth and MsgRequestBatch with legacy amino type format for wallet compatibility
      const aminoTypes = new AminoTypes({
        ...createDefaultAminoConverters(),
        ...auctionClient.auctionAminoConverters,
        ...gravityClient.gravityAminoConverters,
        // Override with legacy amino types that wallets expect
        '/gravity.v1.MsgSendToEth': {
          aminoType: 'gravity/MsgSendToEth',
          toAmino: (msg: MsgSendToEthType): MsgSendToEthAmino => MsgSendToEth.toAmino(msg),
          fromAmino: (obj: MsgSendToEthAmino): MsgSendToEthType => MsgSendToEth.fromAmino(obj),
        },
        '/gravity.v1.MsgRequestBatch': {
          aminoType: 'gravity/MsgRequestBatch',
          toAmino: (msg: MsgRequestBatchType): MsgRequestBatchAmino => MsgRequestBatch.toAmino(msg),
          fromAmino: (obj: MsgRequestBatchAmino): MsgRequestBatchType => MsgRequestBatch.fromAmino(obj),
        },
      });
      return {
        registry,
        aminoTypes,
      };
    },
  };

  return (
    <ChainProvider
      chains={customizedChains}
      assetLists={assets}
      wallets={getSupportedWallets()}
      throwErrors={false}
      signerOptions={signerOptions}
      endpointOptions={
        constants !== null
          ? {
              endpoints: {
                gravitybridge: {
                  rpc: [constants.gravityRpc],
                  rest: [constants.gravityRest],
                },
              },
            }
          : undefined
      }
      walletConnectOptions={{
        signClient: {
          projectId: 'fca94e1d68012104e4056b16caa374bd',
          relayUrl: 'wss://relay.walletconnect.com',
          metadata: {
            name: 'Gravity Station',
            description: 'Unified interface for Gravity Bridge',
            url: 'https://gravity-station.io',
            icons: [],
          },
        },
      }}
    >
      <App />
    </ChainProvider>
  );
}

export function AppProviders(): JSX.Element {
  return (
    <StrictMode>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AppWithChainProvider />
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ErrorBoundary>
    </StrictMode>
  );
}

const rootElement = document.getElementById('root');
if (rootElement === null) {
  throw new Error('Root element not found');
}

createRoot(rootElement).render(<AppProviders />);
